"use client"

import { motion } from "framer-motion"
import { useInView } from "framer-motion"
import { useRef } from "react"
import * as Icons from "lucide-react"

interface Stat {
  icon: string
  value: string
  label: string
  color: string
  description?: string
}

interface StatsGridProps {
  stats: Stat[]
  title?: string
  description?: string
}

// Icon mapping helper
const getIcon = (iconName: string) => {
  const IconComponent = Icons[iconName as keyof typeof Icons]
  return IconComponent as React.ComponentType<{ size?: number; className?: string; strokeWidth?: number }>
}

export function StatsGrid({ stats, title, description }: StatsGridProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1] as const,
      },
    },
  }

  return (
    <section className="relative py-16 sm:py-24 bg-gradient-to-b from-white via-slate-50 to-white overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute inset-0 opacity-40 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-orange-200 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-100 rounded-full blur-3xl"></div>
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)",
            backgroundSize: "50px 50px",
          }}
        ></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Header Section */}
        {(title || description) && (
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            {title && (
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                {title.split(" ").map((word, index) => {
                  const isHighlight = word.toLowerCase().includes("trust") || 
                                     word.toLowerCase().includes("numbers") ||
                                     word.toLowerCase().includes("proven") ||
                                     word.toLowerCase().includes("excellence")
                  return isHighlight ? (
                    <span
                      key={index}
                      className="text-transparent bg-clip-text bg-gradient-to-r from-[#003DA5] to-[#FF7A00]"
                    >
                      {word}{" "}
                    </span>
                  ) : (
                    <span key={index}>{word} </span>
                  )
                })}
              </h2>
            )}
            {description && (
              <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                {description}
              </p>
            )}
          </motion.div>
        )}

        {/* Stats Grid */}
        <motion.div
          className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-${Math.min(stats.length, 6)} gap-4 sm:gap-6 lg:gap-8`}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {stats.map((stat, index) => {
            const StatIcon = getIcon(stat.icon)
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group relative"
              >
                {/* Glow Effect on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-20 blur-2xl transition-opacity duration-500 rounded-3xl`}></div>

                {/* Card */}
                <div className="relative h-full bg-white rounded-2xl sm:rounded-3xl shadow-lg border-2 border-slate-100 hover:border-[#003DA5]/30 transition-all duration-300 p-6 sm:p-8 overflow-hidden">
                  {/* Decorative Corner */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-slate-50 to-transparent rounded-bl-3xl opacity-50"></div>

                  {/* Icon */}
                  <div className="relative mb-4 sm:mb-6">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#003DA5]/10 to-[#FF7A00]/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div
                      className={`relative w-14 h-14 sm:w-16 sm:h-16 mx-auto rounded-2xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}
                    >
                      <StatIcon size={28} className="text-white" strokeWidth={2.5} />
                    </div>
                  </div>

                  {/* Value */}
                  <motion.div
                    className="text-xl text-center sm:text-2xl lg:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#003DA5] to-[#FF7A00] mb-2 sm:mb-3"
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0.5, opacity: 0 }}
                    transition={{ delay: 0.3 + index * 0.1, duration: 0.8, type: "spring" }}
                  >
                    {stat.value}
                  </motion.div>

                  {/* Label */}
                  <div className="text-sm text-center sm:text-base font-bold text-foreground mb-1 uppercase tracking-wide">
                    {stat.label}
                  </div>

                  {/* Description (if provided) */}
                  {stat.description && (
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {stat.description}
                    </p>
                  )}

                  {/* Bottom Accent Line */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#003DA5] via-[#FF7A00] to-[#003DA5] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                </div>
              </motion.div>
            )
          })}
        </motion.div>

        {/* Bottom Decorative Element */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-50 to-orange-50 rounded-full border border-slate-200">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-semibold text-foreground">
              Real-time statistics • Updated daily
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
